import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { agentAPI } from '../../api/agent.api';
import type { AgentDecision, ActionPlan } from '../../types/agent.types';
import './DecisionsView.css';

const OUTCOME_INFO: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  plan_created:       { label: 'Plan cree',       color: '#2563eb', bg: '#dbeafe', icon: '✓' },
  shadow_plan_created:{ label: 'Observation',     color: '#ca8a04', bg: '#fef9c3', icon: '◉' },
  notification_created: { label: 'Notification',  color: '#16a34a', bg: '#dcfce7', icon: '✓' },
  shadow_logged:      { label: 'Observation',     color: '#ca8a04', bg: '#fef9c3', icon: '◉' },
  no_action_claude:   { label: 'Pas d\'action',   color: '#6b7280', bg: '#f3f4f6', icon: '–' },
  sleep_scheduled:    { label: 'En veille',        color: '#6b7280', bg: '#f3f4f6', icon: '◷' },
  skipped_cooldown:   { label: 'En cooldown',      color: '#9ca3af', bg: '#f9fafb', icon: '⏸' },
  skipped_max_plans:  { label: 'Max atteint',      color: '#9ca3af', bg: '#f9fafb', icon: '⏸' },
  skipped_daily_cap:  { label: 'Limite jour',      color: '#9ca3af', bg: '#f9fafb', icon: '⏸' },
  skipped_confidence: { label: 'Pas assez sur',    color: '#f59e0b', bg: '#fef9c3', icon: '?' },
  skipped_gate:       { label: 'Conditions non remplies', color: '#9ca3af', bg: '#f9fafb', icon: '✕' },
  skipped_no_situation: { label: 'Rien a faire',   color: '#9ca3af', bg: '#f9fafb', icon: '–' },
  skipped_active_plan:  { label: 'Plan deja en cours', color: '#9ca3af', bg: '#f9fafb', icon: '⏸' },
  guard_rejected:     { label: 'Regle stricte',    color: '#f59e0b', bg: '#fef9c3', icon: '⚠' },
  error:              { label: 'Erreur',           color: '#dc2626', bg: '#fef2f2', icon: '!' },
  test_run:           { label: 'Test',             color: '#6b7280', bg: '#f3f4f6', icon: '⚙' },
};

function formatTime(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (diffMin < 1) return 'a l\'instant';
  if (diffMin < 60) return `il y a ${diffMin}min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `il y a ${diffH}h`;
  return d.toLocaleDateString('fr-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function situationLabel(id: string) {
  return (id || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export const DecisionsView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [decisions, setDecisions] = useState<AgentDecision[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'actions' | 'skipped'>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const openTracker = useCallback((affaireId: number) => {
    sessionStorage.setItem('agentLeaActiveTab', 'decisions');
    const basePath = location.pathname.includes('/bureau/') ? '/bureau' : '/admin';
    navigate(`${basePath}/tracker/${affaireId}`, {
      state: { from: `${basePath}/agent-lea`, fromAgentLea: true },
    });
  }, [navigate, location.pathname]);

  const fetchDecisions = useCallback(async () => {
    try {
      const params: any = { limit: 100 };
      if (search) params.affaire_id = parseInt(search);
      const response = await agentAPI.getDecisions(params);
      setDecisions(response.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchDecisions();
    const interval = setInterval(fetchDecisions, 15000);
    return () => clearInterval(interval);
  }, [fetchDecisions]);

  const filtered = decisions.filter(d => {
    if (filter === 'actions') return ['plan_created', 'shadow_plan_created', 'notification_created', 'shadow_logged'].includes(d.outcome);
    if (filter === 'skipped') return d.outcome.startsWith('skipped_');
    return true;
  });

  if (loading) return <div className="jrn-loading">Chargement du journal...</div>;

  return (
    <div className="jrn">
      {/* Filters */}
      <div className="jrn-toolbar">
        <div className="jrn-filters">
          <button className={`jrn-filter ${filter === 'all' ? 'jrn-filter--active' : ''}`} onClick={() => setFilter('all')}>
            Tout ({decisions.length})
          </button>
          <button className={`jrn-filter ${filter === 'actions' ? 'jrn-filter--active' : ''}`} onClick={() => setFilter('actions')}>
            Actions ({decisions.filter(d => ['plan_created', 'notification_created', 'shadow_logged'].includes(d.outcome)).length})
          </button>
          <button className={`jrn-filter ${filter === 'skipped' ? 'jrn-filter--active' : ''}`} onClick={() => setFilter('skipped')}>
            Ignores ({decisions.filter(d => d.outcome.startsWith('skipped_')).length})
          </button>
        </div>
        <div className="jrn-search">
          <input
            type="text"
            placeholder="N° affaire..."
            value={search}
            onChange={e => setSearch(e.target.value.replace(/\D/g, ''))}
          />
        </div>
      </div>

      {/* Timeline */}
      <div className="jrn-list">
        {filtered.length === 0 && <div className="jrn-empty">Aucune activite</div>}

        {filtered.map(d => {
          const info = OUTCOME_INFO[d.outcome] || OUTCOME_INFO.error;
          const isExpanded = expanded === d.id;

          return (
            <div
              key={d.id}
              className={`jrn-item ${isExpanded ? 'jrn-item--expanded' : ''}`}
              onClick={() => setExpanded(isExpanded ? null : d.id)}
            >
              <div className="jrn-item-main">
                {/* Outcome icon */}
                <span className="jrn-icon" style={{ color: info.color, backgroundColor: info.bg }}>
                  {info.icon}
                </span>

                {/* Content */}
                <div className="jrn-item-body">
                  <div className="jrn-item-top">
                    <button className="jrn-affaire" onClick={(e) => { e.stopPropagation(); openTracker(d.affaire_id); }}>#{d.affaire_id}</button>
                    <span className="jrn-sep">·</span>
                    <span className="jrn-situation">{situationLabel(d.situation_type)}</span>
                    <span className="jrn-outcome-badge" style={{ color: info.color, backgroundColor: info.bg }}>
                      {info.label}
                    </span>
                  </div>

                  {/* Wake-up schedule for non-action outcomes */}
                  {d.outcome_detail && ['no_action_claude', 'sleep_scheduled', 'skipped_confidence', 'skipped_no_situation'].includes(d.outcome) && (
                    <div className="jrn-wakeup">{d.outcome_detail}</div>
                  )}

                  {/* Reasoning — shown inline if short */}
                  {d.reasoning && (
                    <div className="jrn-reasoning">{d.reasoning}</div>
                  )}
                </div>

                {/* Meta */}
                <div className="jrn-item-meta">
                  {d.confidence !== null && d.confidence !== undefined && (
                    <span className="jrn-confidence">{Math.round(d.confidence * 100)}%</span>
                  )}
                  <span className="jrn-time">{formatTime(d.created_at)}</span>
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="jrn-detail">
                  {d.reasoning && <div className="jrn-detail-row"><strong>Raisonnement:</strong> {d.reasoning}</div>}
                  {d.gate_results && Object.keys(d.gate_results).length > 0 && (
                    <div className="jrn-detail-row">
                      <strong>Conditions:</strong>
                      <div className="jrn-gates">
                        {Object.entries(d.gate_results).map(([key, val]: [string, any]) => (
                          <span key={key} className={`jrn-gate ${val?.passed === false || val?.blocked ? 'jrn-gate--fail' : 'jrn-gate--pass'}`}>
                            {key.replace(/_/g, ' ')} {val?.passed === false || val?.blocked ? '✕' : '✓'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {d.full_prompt && (
                    <div className="jrn-detail-row">
                      <strong>Contexte complet envoye a Claude:</strong>
                      <pre className="jrn-full-prompt">{d.full_prompt}</pre>
                    </div>
                  )}
                  <div className="jrn-detail-meta">
                    {d.cost_usd && <span>Cout: ${Number(d.cost_usd || 0).toFixed(4)}</span>}
                    {d.latency_ms && <span>Duree: {(d.latency_ms / 1000).toFixed(1)}s</span>}
                    {d.plan_id && (
                      <button
                        className="jrn-plan-link"
                        onClick={() => {
                          sessionStorage.setItem('agentLeaActiveTab', 'history');
                          sessionStorage.setItem('agentLeaHighlightPlan', d.plan_id);
                          window.location.reload();
                        }}
                        title={`Voir le plan ${d.plan_id}`}
                      >Plan: {d.plan_id.slice(0, 8)}</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DecisionsView;
